// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import type { ActivityTimelineModel } from '@web/lib/remoteActivityModel';
import { Loader2 } from 'lucide';

export function RecoveryBanner({
  sync,
}: {
  sync: ActivityTimelineModel['sync'];
}) {
  if (sync.state !== 'catching_up') return null;

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-2xl border border-solid border-ds-border-information-subtle-default bg-ds-bg-information-subtle-default px-4 py-3 text-ds-text-information-strong-default"
    >
      <MorphIcon
        icon={Loader2}
        className="mt-0.5 h-4 w-4 shrink-0 animate-spin"
      />
      <div className="min-w-0">
        <p className="m-0 text-body-sm font-semibold">Catching up</p>
        <p className="m-0 mt-0.5 text-body-sm text-ds-text-neutral-muted-default">
          Known activity stays visible while missing events are restored.
        </p>
      </div>
    </div>
  );
}

export function HistoryBoundary({
  sync,
}: {
  sync: ActivityTimelineModel['sync'];
}) {
  if (sync.state !== 'partial') return null;

  return (
    <div className="flex items-center gap-3 py-1" role="note">
      <span className="h-px flex-1 bg-ds-border-neutral-subtle-default" />
      <span className="shrink-0 text-label-xs text-ds-text-neutral-muted-default">
        Earlier activity may be unavailable
      </span>
      <span className="h-px flex-1 bg-ds-border-neutral-subtle-default" />
    </div>
  );
}
